import { DashboardService } from '../../../../../service/events/dashboard.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import { PlanAbonadoFormDefinition } from './form-control/form-definition';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { HttpClient } from '@angular/common/http';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { PlanAbonadoModel } from 'src/app/models/tareas/plan-abonado.model';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import { FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { ComunidadesRegantesModel } from 'src/app/models/analisis/comunidadesRegantes.model';
import { AnalisisApiService } from 'src/app/service/api/analisis-api.service';
import {
  ReactiveFormChangedField,
  ReactiveFormLifecycleEvent,
  ReactiveFormStableValueChange,
  ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

@Component({
  selector: 'app-plan-abonado-form',
  standalone: false,
  templateUrl: './plan-abonado-form.component.html',
  styleUrls: ['./plan-abonado-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanAbonadoFormComponent extends BaseForm<PlanAbonadoModel> implements OnInit, OnDestroy {
  public loadingData = true;

  public form!: PlanAbonadoFormDefinition;

  public formRequest: ReactiveFormRequestCoordinator<PlanAbonadoModel>;

  public getRequest = this.tareasApi.planAbonado.GET;

  public initialPreviousData: PlanAbonadoModel | null = null;

  public reactiveFormRecordId = '';

  public comunidadForm: FormGroup;

  public isReactiveFormReady = false;

  public reactiveFormLoadingError = '';

  public readonly reactiveFormLoadingLabel = 'Preparando plan de abonado...';

  constructor(
    public fincasApi: FincasApiService,
    public siexApi: SiexApiService,
    public tareasApi: TareasApiService,
    public productosApi: ProductosApiService,
    public trabajadoresApi: TrabajadoresApiService,
    public override router: Router,
    public override route: ActivatedRoute,
    public dashboardEvents: DashboardService,
    public clientesApi: ClientesApiService,
    public analisisApi: AnalisisApiService,
    private readonly formBuilder: FormBuilder,
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super(
      route,
      router,
      dashboardEvents,
      tareasApi.planAbonado.PUT,
      tareasApi.planAbonado.POST,
      'plan-abonado',
      'Editar Plan de Abonado',
      'Realizar Plan de Abonado',
      'Duplicar Plan de Abonado'
    );
    this.formRequest = new ReactiveFormRequestCoordinator<PlanAbonadoModel>;
    this.comunidadForm = this.formBuilder.group({
      nombre: ['', [Validators.required, Validators.maxLength(100)]],
      id_usuario: [StorageManager.getUser().id],
      id_cliente: [StorageManager.getClient()?.id ?? ''],
    });
  }

  public async ngOnInit(): Promise<void> {
    try {
      this.resetExternalDropdowns();
      this.softInit(this.getType());
      this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
      const initialModel = this.initializeHostModel(this.getModel() as PlanAbonadoModel);

      this.initialPreviousData = this.getRegisterId() ? this.toPlanAbonadoModel(initialModel) : null;
      this.form = new PlanAbonadoFormDefinition(this.getType(), initialModel as unknown as Record<string, string>, null);
      this.form.requestType = this.getType();
      await this.initFormData();
      this.markReactiveFormReady();
    } catch (error: unknown) {
      this.loadingData = false;
      this.finalizeExternalDropdowns();
      this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
      this.cdr.markForCheck();
      console.error('No se ha podido preparar el plan de abonado.', error);
    }
  }

  public stableValueChanges(event: ReactiveFormStableValueChange<PlanAbonadoModel>): void {
    const changedFieldName = String(event.changedField ?? '');

    const stableValue = this.captureStableValueChange(event.value as PlanAbonadoModel);

    this.form.model = this.toPlanAbonadoModel({
      ...this.form.model,
      ...stableValue,
    });
    this.syncModelFromForm();

    if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
      this.refreshReactiveDropdownMappings();
      return;
    }

    if (event.origin !== 'user') {
      return;
    }

    this.handleUserValueChange(event.value, event.changedField);
  }

  public reactiveFormChanges(
    formModel: ReactiveFormValue<PlanAbonadoModel>,
    tag: ReactiveFormChangedField<PlanAbonadoModel>,
  ): void {
    this.stableValueChanges({
      value: formModel,
      changedField: tag,
      changedFields: [String(tag ?? '')],
      origin: 'user',
    });
  }

  public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<PlanAbonadoModel>): void {
    if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
      return;
    }

    const stableValue = this.captureStableValueChange(event.value as PlanAbonadoModel);

    this.form.model = this.toPlanAbonadoModel({
      ...this.form.model,
      ...stableValue,
    });
    this.syncModelFromForm();
    this.refreshReactiveDropdownMappings();
    this.cdr.markForCheck();
  }

  private handleUserValueChange(
    formModel: ReactiveFormValue<PlanAbonadoModel>,
    tag: ReactiveFormChangedField<PlanAbonadoModel>,
  ): void {
    this.form.model = this.toPlanAbonadoModel(formModel);
    this.form.tag = String(tag ?? '');
    this.form.lastFieldEdited = String(tag ?? '');
    this.syncModelFromForm();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public saveNewOption(): void {
    if (this.comunidadForm.invalid) {
      this.comunidadForm.markAllAsTouched();
      return;
    }

    const endpoint: string = `${this.form.env.serverUrl}/ws/index.php?p1=generic&p2=comunidades_regantes`;
    const payload: ComunidadesRegantesModel = this.comunidadForm.getRawValue();

    this.http.post<ComunidadesRegantesModel>(endpoint, payload)
      .subscribe({
        next: () => {
          this.comunidadForm.reset({
            nombre: '',
            id_usuario: StorageManager.getUser().id,
            id_cliente: StorageManager.getClient()?.id ?? '',
          });

          this.form.openDialog = false;

          pool([
            build(this.analisisApi.comunidadesRegantes.GET, this.form.lists.comunidadesRegantes, 'nombre'),
          ], () => {}).then(() => {
            const newName = payload.nombre;
            const match = this.form.lists.comunidadesRegantes.filtered
              .find((item: any) => item.value && item.value.nombre === newName);

            if (match) {
              const controlName = this.form.formType < 3 ? 'id_comunidad_regantes' : 'comunidad_regantes';
              const control = this.formBuilderRef?.model?.get(controlName);

              if (control) {
                control.patchValue(match.value?.id, { emitEvent: false });
                control.markAsPristine();
              }
            }

            this.refreshReactiveDropdownMappings();
            this.cdr.markForCheck();
          });
        },
        error: (error: unknown) => {
          console.error('Error creating comunidad_regantes:', error);
        },
      });
  }

  private initFormData(): Promise<void> {
    const buildsRequest = this.getDropdownBuildsForConfiguredCatalogs();

    return pool(buildsRequest, () => {
      this.formRequest.executePhase(FormPhase.UPDATE);
    }).then(() => {
      this.finalizeExternalDropdowns();
      this.refreshReactiveDropdownMappings();
      this.loadingData = false;
      this.cdr.markForCheck();
    }).catch((error: unknown) => {
      this.finalizeExternalDropdowns();
      throw error;
    });
  }


  private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
    const configuredCatalogs = this.getConfiguredDropdownCatalogs();
    const buildsRequest: ReturnType<typeof build>[] = [];

    if (configuredCatalogs.has('id_cultivo') || configuredCatalogs.has('id_cultivo_precedente')) {
      buildsRequest.push(build(this.siexApi.cultivosGET, [
        this.form.lists.cultivos,
        this.form.lists.cultivos_precedentes,
      ], 'cultivo', true));
    }

    if (configuredCatalogs.has('id_comunidad_regantes')) {
      buildsRequest.push(build(this.analisisApi.comunidadesRegantes.GET, this.form.lists.comunidadesRegantes, 'nombre'));
    }

    return buildsRequest;
  }

  private getConfiguredDropdownCatalogs(): Set<string> {
    const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

    return new Set([
      ...(dropdownCatalogs?.critical ?? []),
      ...(dropdownCatalogs?.deferred ?? []),
      ...(dropdownCatalogs?.lazy ?? []),
    ]);
  }

  private markReactiveFormReady(): void {
    this.isReactiveFormReady = true;
    this.cdr.detectChanges();
    this.finalizeExternalDropdowns();
    this.refreshReactiveDropdownMappings();
    this.cdr.markForCheck();
  }

  private getReadableInitialisationError(error: unknown): string {
    if (error instanceof Error && error.message.trim() !== '') {
      return 'No se ha podido cargar el plan de abonado: ' + error.message;
    }

    return 'No se ha podido cargar el plan de abonado. Revisa la conexión e inténtalo de nuevo.';
  }

  private refreshReactiveDropdownMappings(): void {
    this.formBuilderRef?.refreshExternalDropdownMappings();
  }

  private syncModelFromForm(): void {
    const normalizedModel = this.toPlanAbonadoModel(this.form.model);

    this.form.model = normalizedModel;
    this.updateHostModel(normalizedModel);
  }

  private toPlanAbonadoModel(
    sourceModel: ReactiveFormValue<PlanAbonadoModel> | Partial<PlanAbonadoModel> | Record<string, unknown>,
  ): PlanAbonadoModel {
    const normalizedModel: Record<string, PlanAbonadoModel[keyof PlanAbonadoModel]> = {};

    for (const [key, value] of Object.entries(sourceModel)) {
      if (value !== undefined) {
        normalizedModel[key] = value as PlanAbonadoModel[keyof PlanAbonadoModel];
      }
    }

    return normalizedModel as PlanAbonadoModel;
  }
}
